import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';
import TypeVoidCivilUnionList from 'shell/components/shared/type-void-civil-union-list';
import CourtTypeList from 'shell/components/shared/court-type-list';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { AccordionVoidCivilUnion } from 'shell/components/shared/sipo-person-form/new-person';
import PmiDivider from 'shell/components/shared/divider';

interface FormVoidCivilUnionProps {
  values?: AccordionVoidCivilUnion;
  errors?: FormikErrors<AccordionVoidCivilUnion>;
  touched?: FormikTouched<AccordionVoidCivilUnion>;
  onChange: (
    key: keyof AccordionVoidCivilUnion,
    value: AccordionVoidCivilUnion[keyof AccordionVoidCivilUnion]
  ) => void;
  onFieldsChange: (values: AccordionVoidCivilUnion) => void;
}

const FormVoidCivilUnionComponent = ({
  values,
  onChange,
  errors,
  touched,
}: FormVoidCivilUnionProps): React.ReactElement => {
  const handleTypeVoidCivilUnionListChange = useEventCallback((type: string | undefined) => {
    onChange('type', type);
  });

  const handleCourtTypeListChange = useEventCallback((courtType: string | undefined) => {
    onChange('courtType', courtType);
  });

  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('municipality', municipality);
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TypeVoidCivilUnionList
          label="Motivo scioglimento"
          aria-label="Motivo scioglimento"
          value={values?.type || ''}
          onChange={handleTypeVoidCivilUnionListChange}
          error={touched?.type && Boolean(errors?.type)}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Sentenza</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Numero"
          aria-label="Numero"
          id="sentence-number"
          fullWidth
          inputProps={{
            maxLength: '20',
          }}
          helperText={errors?.number}
          error={Boolean(errors?.number) && touched?.number}
          value={values?.number || ''}
          onChange={(event) => {
            onChange('number', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data sentenza *"
          aria-label="Data sentenza"
          value={values?.eventDate || null}
          onChange={(date) => {
            onChange('eventDate', date);
          }}
          renderInput={(parameters) => (
            <PmiTextField
              {...parameters}
              id="eventDate"
              fullWidth
              helperText={errors?.eventDate}
              error={Boolean(errors?.eventDate) && touched?.eventDate}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data validità *"
          aria-label="Data validità"
          value={values?.validityDate || null}
          onChange={(date) => {
            onChange('validityDate', date);
          }}
          renderInput={(parameters) => (
            <PmiTextField
              {...parameters}
              id="validityDate"
              fullWidth
              helperText={errors?.validityDate}
              error={Boolean(errors?.validityDate) && touched?.validityDate}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Autorità *"
          aria-label="Autorità"
          id="authority"
          fullWidth
          error={touched?.authority && Boolean(errors?.authority)}
          helperText={errors?.authority}
          inputProps={{
            maxLength: '120',
          }}
          value={values?.authority || ''}
          onChange={(event) => {
            onChange('authority', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CourtTypeList
          label="Tipo tribunale"
          aria-label="Tipo tribunale"
          error={Boolean(errors?.courtType) && touched?.courtType}
          value={values?.courtType || ''}
          onChange={handleCourtTypeListChange}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Atto</PmiTitleDivider>
      </Grid>
      <Grid item container spacing={2} xs={12} alignItems="center">
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data atto"
            aria-label="Data atto"
            value={values?.documentDate || null}
            onChange={(date) => {
              onChange('documentDate', date);
            }}
            renderInput={(parameters) => (
              <PmiTextField
                {...parameters}
                id="documentDate"
                fullWidth
                error={touched?.documentDate && Boolean(errors?.documentDate)}
                helperText={errors?.documentDate}
              />
            )}
          />
        </Grid>
        <Grid item xs>
          <FormControlLabel
            control={
              <Checkbox
                id="documentTranscript"
                checked={values?.transcript || false}
                onChange={() => onChange('transcript', !values?.transcript)}
                inputProps={{ 'aria-label': 'Atto trascritto' }}
              />
            }
            label="Atto trascritto"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={9}>
        <MunicipalityList
          label="Comune"
          aria-label="Comune"
          error={Boolean(errors?.municipality) && touched?.municipality}
          value={values?.municipality || null}
          onChange={handleMunicipalityListChange}
          fromDate={values?.documentDate}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Provincia"
          id="province"
          inputProps={{
            maxLength: '2',
          }}
          aria-label="Provincia"
          fullWidth
          value={values?.municipality?.province || ''}
          onChange={(event) => {
            onChange('province', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Anno"
          aria-label="Anno"
          id="year"
          fullWidth
          inputProps={{
            maxLength: '4',
          }}
          value={values?.year || ''}
          error={touched?.year && Boolean(errors?.year)}
          helperText={errors?.year}
          onChange={(event) => {
            onChange('year', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Nr.Atto"
          aria-label="Nr.Atto"
          id="document-number"
          fullWidth
          inputProps={{
            maxLength: '20',
          }}
          value={values?.documentNumber || ''}
          error={touched?.documentNumber && Boolean(errors?.documentNumber)}
          helperText={errors?.documentNumber}
          onChange={(event) => {
            onChange('documentNumber', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Parte"
          aria-label="Parte"
          id="portion"
          fullWidth
          inputProps={{
            maxLength: '5',
          }}
          value={values?.portion || ''}
          error={touched?.portion && Boolean(errors?.portion)}
          helperText={errors?.portion}
          onChange={(event) => {
            onChange('portion', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Serie"
          aria-label="Serie"
          id="range"
          fullWidth
          inputProps={{
            maxLength: '10',
          }}
          value={values?.range || ''}
          error={touched?.range && Boolean(errors?.range)}
          helperText={errors?.range}
          onChange={(event) => {
            onChange('range', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Reg/Vol"
          aria-label="Reg/Vol"
          id="reg-vol"
          fullWidth
          inputProps={{
            maxLength: '5',
          }}
          value={values?.regVol || ''}
          error={touched?.regVol && Boolean(errors?.regVol)}
          helperText={errors?.regVol}
          onChange={(event) => {
            onChange('regVol', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Ufficio municipio"
          aria-label="Ufficio municipio"
          id="office"
          fullWidth
          inputProps={{
            maxLength: '50',
          }}
          value={values?.office || ''}
          error={touched?.office && Boolean(errors?.office)}
          helperText={errors?.office}
          onChange={(event) => {
            onChange('office', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormVoidCivilUnionComponent.displayName = 'Form void civil union component';

const FormVoidCivilUnion = memo(FormVoidCivilUnionComponent);

export default FormVoidCivilUnion;
